import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Select,
  Divider,
  InputNumber,
  DatePicker,
  Space,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import { fetchData, generateReport } from "../../../features/fetch/fetchSlice";
import dayjs from "dayjs";
import Images from "./Images";

const { TextArea } = Input;
const ProgressAdd = () => {
  const urlProjects = "main_project/fetch_mainprojects";
  const urlPathway = "api/v2/irrigationPathway/get";
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { activeObjMontProj, dataProjects, dataPathway } = useSelector(
    (state) => ({
      activeObjMontProj: state.obj.activeObj["montProj"],
      dataProjects: state.fetch.data[urlProjects] || [],
      dataPathway: state.fetch.data[urlPathway] || [],
    }),
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  async function handleCancel() {
    await navigate("/rob/project-progress");
    await setDownload(false);
  }

  const [download, setDownload] = useState(false);
  const downloadRef = useRef(false);
  const [submitting, setSubmitting] = useState(null);
  const handleDownload = async (item) => {
    const res = await dispatch(
      generateReport({
        url: "api/v1/reports/directReport",
        reportName: "MnE_REPORT",
        itemId: item,
        reportFormat: "PDF",
      }),
    );
    if (res?.payload) {
      const blobData = res.payload.data;
      const headers = res.payload.headers;

      const contentType = headers["content-type"] || "application/octet-stream";
      const contentDisposition = headers["content-disposition"];

      let fileName = "report";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }

      const blob = new Blob([blobData], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      await toast.success(`${fileName} downloaded successfully`);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error("Failed to download file");
    }
  };

  const onFinish = async (data) => { 
    data.projId = activeObjMontProj?.projId ? activeObjMontProj?.projId : null;

    data.projPathwayId = Array.isArray(data?.projPathwayId)
      ? data?.projPathwayId.join(",")
      : data?.projPathwayId;

    data.projImages = Array.isArray(data?.projImages)
      ? data?.projImages
      : data?.projImages ? [data.projImages] : [];

    setSubmitting(downloadRef.current ? "report" : "save");
    const res = await dispatch(
      save({
        ...data,
        url: "project/save_project",
      }),
    );
    if (res?.payload?.success) {
      if (downloadRef.current) {
        await handleDownload(res?.payload?.data?.projId);
        downloadRef.current = false;
        setDownload(false);
      }
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
    setSubmitting(null);
  };

  const initialFormValues = {
    ...activeObjMontProj,
    projStartDate: activeObjMontProj?.projStartDate
      ? dayjs(new Date(activeObjMontProj?.projStartDate))
      : null,
    projExpectedEndDate: activeObjMontProj?.projExpectedEndDate
      ? dayjs(new Date(activeObjMontProj?.projExpectedEndDate))
      : null,
    projRevisedEndDate: activeObjMontProj?.projRevisedEndDate
      ? dayjs(new Date(activeObjMontProj?.projRevisedEndDate))
      : null,
    projEvalDate: activeObjMontProj?.projEvalDate
      ? dayjs(new Date(activeObjMontProj?.projEvalDate))
      : null,
  };

  useEffect(() => {
    form.setFieldsValue({
      ...activeObjMontProj,
      projStartDate: activeObjMontProj?.projStartDate
        ? dayjs(new Date(activeObjMontProj?.projStartDate))
        : null,
      projExpectedEndDate: activeObjMontProj?.projExpectedEndDate
        ? dayjs(new Date(activeObjMontProj?.projExpectedEndDate))
        : null,
      projRevisedEndDate: activeObjMontProj?.projRevisedEndDate
        ? dayjs(new Date(activeObjMontProj?.projRevisedEndDate))
        : null,
      projEvalDate: activeObjMontProj?.projEvalDate
        ? dayjs(new Date(activeObjMontProj?.projEvalDate))
        : null,
    });
  }, [activeObjMontProj, form]);

  const handleDatesChange = (_, allValues) => {
    const { projStartDate } = allValues;

    if (projStartDate) {
      const start = dayjs(projStartDate);
      const today = dayjs();
      const diff = today.diff(start, "day");

      form.setFieldsValue({
        projPercentageTimeLapse: `${diff} day${diff !== 1 ? "s" : ""} elapsed`,
      });
    } else {
      form.setFieldsValue({ projPercentageTimeLapse: "" });
    }
  };

  const handleProjectNumberChange = (value) => {
    const selectedProject = dataProjects?.find(
      (item) => item?.projectName === value,
    );

    if (selectedProject) {
      form?.setFieldsValue({
        projProjectId: selectedProject?.projectId,
        projNumber: selectedProject?.projectNumber || "",
        projStatus: selectedProject?.projectStatus || "",
        projGok: selectedProject?.projectGovernmentAmount || "",
        projForeign: selectedProject?.projectDonorsAmount || "",
        projBank: selectedProject?.projectBankAmount || "",

        projPathwayId: selectedProject?.projectPathwayId
          ?.split(",")
          ?.map((b) => b?.trim()),
        projCompletionRate: selectedProject?.projectCompletionRate || "",

        projStartDate: selectedProject?.projectStartDate
          ? dayjs(new Date(selectedProject?.projectStartDate))
          : null,
        projExpectedEndDate: selectedProject?.projectEndDate
          ? dayjs(new Date(selectedProject?.projectEndDate))
          : null,
        projRevisedEndDate: selectedProject?.projectRevisedEndDate
          ? dayjs(new Date(selectedProject?.projectRevisedEndDate))
          : null,
        projTotalCost: selectedProject?.projectCost ? Number(selectedProject.projectCost) : null,
      });
      handleDatesChange(null, {
        projStartDate: selectedProject?.projectStartDate
          ? dayjs(new Date(selectedProject?.projectStartDate))
          : null,
      });
    } else {
      form?.setFieldsValue({
        projNumber: "",
        projStatus: "",
        projStartDate: "",
        projExpectedEndDate: "",
        projRevisedEndDate: "",
        projTotalCost: null,
      });
    }
  };

  useEffect(() => {
    dispatch(
      fetchData({
        url: urlPathway,
        limit: null,
        start: 0,
      }),
    );
  }, []);
  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          { title: "Project Progress", path: "/rob/project-progress" },
          { title: "Progress Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="form-add-h mb-[1.25rem]">New Project Progress</div>
        <Divider />
        <div className="w-full mt-[1.75rem]">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
            onValuesChange={(_, allValues) => {
              const {
                projTotalCost,
                projActualCummulative,
                projActualExpenditure,
                projAllocationFyn,
              } = allValues;

              const totalCost = Number(projTotalCost);
              const actualCummulative = Number(projActualCummulative);

              if (
                !isNaN(totalCost) && projTotalCost != null &&
                !isNaN(actualCummulative) && projActualCummulative != null
              ) {
                const balance = totalCost - actualCummulative;
                form.setFieldsValue({
                  projOustandingBal: balance >= 0 ? balance : 0,
                });
              }

              if (
                typeof projActualExpenditure === "number" &&
                typeof projAllocationFyn === "number" &&
                projAllocationFyn !== 0
              ) {
                const absorption =
                  (projActualExpenditure / projAllocationFyn) * 100;
                form.setFieldsValue({
                  projAbsorption: parseFloat(absorption.toFixed(2)),
                });
              } else {
                form.setFieldsValue({
                  projAbsorption: 0,
                });
              }
            }}
          >
            <div className="custom-title">Project Details</div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="projName"
                className=""
                label={"Project Name"}
                rules={[
                  {
                    required: true,
                    message: "Please select project name",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={dataProjects?.map((item, index) => {
                    return {
                      value: item?.projectName,
                      label: item?.projectName,
                    };
                  })}
                  onChange={handleProjectNumberChange}
                  onDropdownVisibleChange={() => {
                    dispatch(
                      fetchData({
                        url: urlProjects,
                        projectStatus: "ONGOING",
                        start: 0,
                        limit: null,
                      }),
                    );
                  }}
                  showSearch
                  onSearch={(value) => {
                    dispatch(
                      fetchData({
                        url: urlProjects,
                        start: 0,
                        limit: null,
                        projectName: value || null,
                        projectStatus: "ONGOING",
                      }),
                    );
                  }}
                  optionFilterProp="label"
                  filterOption={false}
                />
              </Form.Item>
              <Form.Item
                name="projProjectId"
                className="hidden"
                label={"Project Id"}
                rules={[
                  {
                    required: true,
                    message: "Please select project id",
                  },
                ]}
              >
                <Input disabled className="input " />
              </Form.Item>
              <Form.Item
                name="projNumber"
                className=""
                label={"Project Number"}
                rules={[
                  {
                    required: true,
                    message: "Please select project number",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
              <Form.Item
                name="projStatus"
                className=""
                label={"Project Status"}
                rules={[
                  {
                    required: true,
                    message: "Please select project status",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>

              <Form.Item
                label={"Pathways"}
                name="projPathwayId"
                rules={[
                  {
                    required: true,
                    message: "Please add pathways",
                  },
                ]}
              >
                <Select
                  disabled
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following pathways
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase(),
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataPathway?.length > 0 &&
                    dataPathway?.map((item) => (
                      <Select.Option
                        key={item?.irrigationPathwayId}
                        value={item?.irrigationPathwayId}
                      >
                        {item?.irrigationPathwayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </div>

            <div className="custom-title mt-[1.25rem]">Timelines</div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                label="Start Date"
                name="projStartDate"
                rules={[
                  {
                    required: true,
                    message: "Start date is required",
                  },
                ]}
              >
                <DatePicker disabled format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="Expected End Date"
                name="projExpectedEndDate"
                rules={[
                  {
                    required: true,
                    message: "Expected end date is required",
                  },
                ]}
              >
                <DatePicker disabled format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="Revised End Date"
                name="projRevisedEndDate"
                rules={[
                  {
                    required: false,
                    message: "Revised end date is required",
                  },
                ]}
              >
                <DatePicker disabled format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                name="projPercentageTimeLapse"
                label={"Time elapsed"}
                rules={[
                  {
                    required: true,
                    message: "Please add time elapsed",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
            </div>

            <div className="custom-title mt-[1.25rem]">
              Estimated Project Cost
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="projTotalCost"
                label="Total Project Cost"
                rules={[
                  {
                    required: true,
                    message: "Please add total project cost",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  suffix="Kes."
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="projForeign"
                label="Foreign"
                rules={[
                  {
                    required: false,
                    message: "Please add foreign",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  suffix="Kes."
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="projGok"
                label="GOK"
                rules={[
                  {
                    required: false,
                    message: "Please add GOK",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  suffix="Kes."
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="projBank"
                label="Bank"
                rules={[
                  {
                    required: false,
                    message: "Please add bank",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  suffix="Kes."
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="projTotalAllocation"
                label={"Total Allocation to date"}
                rules={[
                  {
                    required: true,
                    message: "Please add total allocation to date",
                  },
                ]}
              >
                <InputNumber
                  suffix="Kes."
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="projActualCummulative"
                label={"Actual Cummulative Exp. (as at 30th June FY N-1)"}
                rules={[
                  {
                    required: true,
                    message:
                      "Please add actual cummulative Exp. (as at 30th June FY N-1)",
                  },
                ]}
              >
                <InputNumber
                  suffix="Kes."
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                  onChange={(val) => {
                    const totalCost = form.getFieldValue("projTotalCost");
                    if (totalCost != null && val != null) {
                      const balance = Number(totalCost) - Number(val);
                      form.setFieldsValue({ projOustandingBal: balance >= 0 ? balance : 0 });
                    }
                  }}
                />
              </Form.Item>
              <Form.Item
                name="projOustandingBal"
                label={
                  "Outstanding Balance (Total Project Cost - Actual Cummulative Exp. (as at 30th June FY N-1))"
                }
                rules={[
                  {
                    required: true,
                    message:
                      "Please add outstanding balance  (as at 30th June FY N-1)",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  suffix="Kes."
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
            </div>

            <div className="custom-title mt-[1.25rem]">
              Actual Physical Progress
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="projExpectedOutput"
                label={"Expected Output at inception"}
                rules={[
                  {
                    required: true,
                    message: "Please add expected output at inception",
                  },
                ]}
              >
                <TextArea className="text_area" />
              </Form.Item>
              <Form.Item
                name="projActualAchievedOutput"
                label={"Actual Achieved Output to date"}
                rules={[
                  {
                    required: true,
                    message: "Please add actual achieved output to date",
                  },
                ]}
              >
                <TextArea className="text_area" />
              </Form.Item>
              <Form.Item
                name="projCompletionRate"
                label={"Project Completion Rate to date"}
                rules={[
                  {
                    required: true,
                    message: "Please add Project Completion Rate to date",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  max="100"
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="projApprovedBudget"
                label={"Approved Budget (FY N)"}
                rules={[
                  {
                    required: false,
                    message: "Please add approved budget (FY N)",
                  },
                ]}
              >
                <InputNumber
                  suffix="Kes."
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="projActualExpenditure"
                label={"Actual Expenditure (FY N)"}
                rules={[
                  {
                    required: true,
                    message: "Please add Actual Expenditure (FY N)",
                  },
                ]}
              >
                <InputNumber
                  onChange={(val) => {
                    const allocation = form.getFieldValue("projAllocationFyn");
                    if (val != null && allocation != null && Number(allocation) !== 0) {
                      const absorption = (Number(val) / Number(allocation)) * 100;
                      form.setFieldsValue({ projAbsorption: parseFloat(absorption.toFixed(2)) });
                    } else {
                      form.setFieldsValue({ projAbsorption: 0 });
                    }
                  }}
                  suffix="Kes."
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="projAllocationFyn"
                label={"Allocation (FYN)"}
                rules={[
                  {
                    required: true,
                    message: "Please add total allocation(Fyn)",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const actualExpenditure = getFieldValue(
                        "projActualExpenditure",
                      );
                      if (
                        value === undefined ||
                        actualExpenditure === undefined ||
                        value > actualExpenditure
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          "Allocation (FYN) must be greater than Actual Expenditure (FY N)",
                        ),
                      );
                    },
                  }),
                ]}
              >
                <InputNumber
                  suffix="Kes."
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                  onChange={(val) => {
                    const expenditure = form.getFieldValue("projActualExpenditure");
                    if (expenditure != null && val != null && Number(val) !== 0) {
                      const absorption = (Number(expenditure) / Number(val)) * 100;
                      form.setFieldsValue({ projAbsorption: parseFloat(absorption.toFixed(2)) });
                    } else {
                      form.setFieldsValue({ projAbsorption: 0 });
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                name="projAbsorption"
                label="Absorption (Actual Expenditure (FY N) /allocation  *100)"
                rules={[
                  {
                    required: true,
                    message: "Please add absorption",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  suffix="%"
                  min={0}
                  max={100}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="projTargetedExpOutput"
                label={"Targeted Expected Outputs (FYN)"}
                rules={[
                  {
                    required: true,
                    message: "Please add targeted expected outputs",
                  },
                ]}
              >
                <TextArea className="text_area" />
              </Form.Item>
              <Form.Item
                name="projCompletionRateAtReport"
                label={"Project Completion rate as at Date of report"}
                rules={[
                  {
                    required: true,
                    message:
                      "Please add Project Completion rate as at Date of report",
                  },
                ]}
              >
                <InputNumber
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="projRisks"
                label={"Project Risks"}
                rules={[
                  {
                    required: true,
                    message: "Please add Project Risks",
                  },
                ]}
              >
                <TextArea
                  placeholder="Project Risks..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="projChallenges"
                label={"Challenges/Emerging Issues"}
                rules={[
                  {
                    required: true,
                    message: "Please add Challenges/Emerging Issues",
                  },
                ]}
              >
                <TextArea
                  placeholder="Challenges/Emerging Issues..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="projRecommandations"
                label={"Recommendation"}
                rules={[
                  {
                    required: true,
                    message: "Please add Reccomendation",
                  },
                ]}
              >
                <TextArea
                  placeholder="Reccomendation..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
              <Form.Item
                name="projRemarks"
                label={"Remarks/Discussion"}
                rules={[
                  {
                    required: true,
                    message: "Please add Remarks/Discussion",
                  },
                ]}
              >
                <TextArea
                  placeholder="Remarks/Discussion..."
                  rows={5}
                  className="text_area"
                />
              </Form.Item>
            </div>

            <div className="custom-title mt-[1.25rem]">Projection</div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="projProjectionN1"
                label={"FY N+1"}
                rules={[
                  {
                    required: true,
                    message: "Please add FY N+1",
                  },
                ]}
              >
                <InputNumber
                  suffix="Kes."
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="projProjectionN2"
                label={"FY N+2"}
                rules={[
                  {
                    required: true,
                    message: "Please add FY N+2",
                  },
                ]}
              >
                <InputNumber
                  suffix="Kes."
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="projProjectionN3"
                label={"FY N+3"}
                rules={[
                  {
                    required: true,
                    message: "Please add FY N+3",
                  },
                ]}
              >
                <InputNumber
                  suffix="Kes."
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                label="Inspection Date"
                name="projEvalDate"
                rules={[
                  {
                    required: true,
                    message: "Inspection date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
            </div>

            <div className="custom-title mt-[1.25rem]">Uploaded Images</div>
            <Form.Item name="projImages" hidden>
              <Select mode="multiple" />
            </Form.Item>
            <Images form={form} existingImages={activeObjMontProj?.projImages} />

            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>
                <div className="w-[200px]">
                  <button
                    disabled={submitting !== null}
                    type="button"
                    onClick={() => {
                      downloadRef.current = true;
                      setDownload(true);
                      form.validateFields()
                        .then((values) => onFinish(values))
                        .catch(() => toast.error("Please fill in all required fields"));
                    }}
                    className="cstm-btn"
                  >
                    {submitting === "report" ? (
                      <Spin className="blu_spin" />
                    ) : (
                      "Generate Report"
                    )}
                  </button>
                </div>
                <div className="w-[150px]">
                  <button
                    disabled={submitting !== null}
                    key="submit"
                    type="button"
                    className="cstm-btn-2"
                    onClick={() => {
                      downloadRef.current = false;
                      setDownload(false);
                      form.validateFields()
                        .then((values) => onFinish(values))
                        .catch(() => toast.error("Please fill in all required fields"));
                    }}
                  >
                    {submitting === "save" ? (
                      <Spin />
                    ) : activeObjMontProj?.projId ? (
                      "Update"
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default ProgressAdd;
